import { useCallback, useMemo, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { classNames } from "primereact/utils";
import { InputText } from "primereact/inputtext";
import { AutoComplete } from "primereact/autocomplete";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import styles from "./style.module.scss";

const MOCK_PARTNERS = [
    {
        id: 1,
        fullName: "Иванов Иван Иванович",
        iin: "990101123456",
        status: "ACTIVE",
        personProfile: {
            residency: "RESIDENT",
            specialStatus: ["STANDARD_DEDUCTION", "SOCIAL_TAX_DEDUCTION"],
        },
    },
    {
        id: 2,
        fullName: "Петров Петр Петрович",
        iin: "880202654321",
        status: "ACTIVE",
        personProfile: null,
    },
];

const formatISODate = (date) => {
    if (!date) {
        return null;
    }

    const d = new Date(date);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");

    return `${y}-${m}-${day}`;
};

const formatMoneyKZT = (value) => {
    const n = Number(value || 0);
    return `${new Intl.NumberFormat("ru-RU").format(n)} ₸`;
};

const mockCalculate = async ({ selectionPanel }) => {
    await new Promise((r) => setTimeout(r, 300));

    if (!selectionPanel?.personProfile) {
        return {
            error: {
                code: "MISSING_DATA",
                message: "Данные для корректного расчета отсутствуют",
                details: [],
            },
        };
    }

    return {
        summary: {
            grossAmount: 450000,
            grossRetention: 113850,
            total: 336150,
            currency: "KZT",
        },
        breakdown: {
            opv: { base: 150000, rate: 0.1, amount: 15000 },
            vosms: { base: 150000, rate: 0.02, amount: 3000 },
            so: { base: 150000, rate: 0.05, amount: 7500 },
            ipn: { base: 124500, rate: 0.1, amount: 12450 },
        },
        meta: {
            calculatedAt: new Date().toISOString(),
            formulaVersion: "2025.1",
        },
    };
};

const QUESTIONNAIRE_ITEMS = [
    { type: "residency", value: "RESIDENT", label: "Резидент" },
    { type: "residency", value: "NON_RESIDENT", label: "Нерезидент" },
    { type: "specialStatus", value: "OLD_AGE_PENSIONER", label: "Пенсионер по возрасту" },
    { type: "specialStatus", value: "SERVICE_PENSIONER", label: "Пенсионер по выслуге лет" },
    { type: "specialStatus", value: "STUDENT", label: "Студент" },
    { type: "specialStatus", value: "DISABLED_GROUP_1_2_3", label: "Инвалид 1,2,3 группы" },
    { type: "specialStatus", value: "DISABLED_GROUP_1_2_LIFE", label: "Инвалид 1,2 группы бессрочно" },
    { type: "specialStatus", value: "PARENT_GUARDIAN_DISABLED_PERSON", label: "Родитель (опекун) инвалида" },
    { type: "specialStatus", value: "STANDARD_DEDUCTION", label: "Стандартный вычет" },
    { type: "specialStatus", value: "SOCIAL_TAX_DEDUCTION", altValue: "Social tax deduction", label: "Социальный налоговый вычет" },
];

const initialValues = {
    partner: "",
    iin: "",
    status: "",
    periodFrom: null,
    periodTo: null,
};

const ClcPaymentCalculator = () => {
    const toastRef = useRef(null);

    const [partnerSuggestions, setPartnerSuggestions] = useState([]);
    const [selectedPartner, setSelectedPartner] = useState(null);
    const [calcResult, setCalcResult] = useState(null);
    const [globalError, setGlobalError] = useState("");
    const [isCalculating, setIsCalculating] = useState(false);

    const [isSaved, setIsSaved] = useState(false);
    const [isApproved, setIsApproved] = useState(false);

    const {
        control,
        reset,
        setValue,
        getValues,
        clearErrors,
        setError,
        trigger,
        formState: { errors },
    } = useForm({
        mode: "onSubmit",
        reValidateMode: "onSubmit",
        shouldFocusError: true,
        defaultValues: initialValues,
    });

    const isQuestionnaireActive = useCallback(
        (item) => {
            const profile = selectedPartner?.personProfile;

            if (!profile) {
                return item.type === "residency" && item.value === "RESIDENT";
            }

            if (item.type === "residency") {
                return profile.residency === item.value;
            }

            if (item.type === "specialStatus") {
                const list = Array.isArray(profile.specialStatus) ? profile.specialStatus : [];
                return list.includes(item.value) || (item.altValue && list.includes(item.altValue));
            }

            return false;
        },
        [selectedPartner]
    );

    const onPartnerSearch = useCallback((e) => {
        const query = (e.query || "").toLowerCase().trim();

        if (!query) {
            setPartnerSuggestions([]);
            return;
        }

        setPartnerSuggestions(
            MOCK_PARTNERS
                .filter((partner) => (partner.fullName || "")
                    .toLowerCase()
                    .includes(query)
                )
        );
    }, []);

    const resetFlowOnSelectionChange = useCallback(() => {
        setCalcResult(null);
        setGlobalError("");
        setIsSaved(false);
        setIsApproved(false);
    }, []);

    const onPartnerInputChange = useCallback(
        (nextValue) => {
            setValue("partnerName", nextValue, { shouldDirty: true });
            setSelectedPartner(null);
            setValue("iin", "", { shouldDirty: true });
            setValue("status", "", { shouldDirty: true });

            clearErrors(["partnerName"]);
            resetFlowOnSelectionChange();
        },
        [setValue, clearErrors, resetFlowOnSelectionChange]
    );

    const onPartnerSelect = useCallback((partner) => {
        setValue("partner", partner, { shouldDirty: true, shouldValidate: true });
        setValue("iin", partner?.iin || "", { shouldDirty: true });
        setValue("status", partner?.status || "", { shouldDirty: true });

        setCalcResult(null);
        setGlobalError("");
        setIsSaved(false);
        setIsApproved(false);

        clearErrors(["partner", "periodFrom", "periodTo"]);
    }, [setValue, clearErrors]);

    const applyApiErrors = useCallback((details = []) => {
        details.forEach((d) => {
            if (!d?.field) {
                return;
            }

            switch (d.field) {
                case "period.to":
                    setError("periodTo", { type: "server", message: d.message });
                    break;
                case "period.from":
                    setError("periodFrom", { type: "server", message: d.message });
                    break;
                case "partnerName":
                    setError("partner", { type: "server", message: d.message });
                    break;
            }
        });
    }, [setError]);

    const validateRequiredForCalculate = useCallback(async () => {
        const ok = await trigger(["partnerName", "periodFrom", "periodTo"]);

        if (!selectedPartner) {
            setError("partnerName", { type: "validate", message: "" });
            setGlobalError("Данные для корректного расчета отсутствуют");

            return false;
        }

        if (!ok) {
            setGlobalError("Данные для корректного расчета отсутствуют");

            return false;
        }

        const { periodFrom, periodTo } = getValues();

        if (periodFrom && periodTo && new Date(periodTo) < new Date(periodFrom)) {
            setError("periodTo", {
                type: "validate",
                message: "Дата окончания не может быть раньше даты начала",
            });
            setGlobalError("");

            return false;
        }

        setGlobalError("");

        return true;
    }, [trigger, getValues, setError, selectedPartner]);

    const handleCalculate = useCallback(async () => {
        clearErrors(["periodTo"]);
        const valid = await validateRequiredForCalculate();

        if (!valid) {
            return;
        }

        const { partner, periodFrom, periodTo } = getValues();

        setIsCalculating(true);
        setCalcResult(null);
        setIsSaved(false);
        setIsApproved(false);

        const payload = {
            selectionPanel: {
                partnerName: partner?.fullName ?? "",
                iin: partner?.iin ?? "",
                status: partner?.status ?? "",
                period: {
                    from: formatISODate(periodFrom),
                    to: formatISODate(periodTo),
                },
            },
            personProfile: partner?.personProfile ?? null,
        };

        const res = await mockCalculate(payload);

        setIsCalculating(false);

        if (res?.error) {
            if (res.error.code === "VALIDATION_ERROR") {
                applyApiErrors(res.error.details || []);
            }

            setGlobalError(res.error.message || "Данные для корректного расчета отсутствуют");
            setCalcResult(null);
            return;
        }

        setGlobalError("");
        setCalcResult(res);

        toastRef.current?.show({
            severity: "success",
            summary: "Готово",
            detail: "Расчет выполнен",
            life: 2500,
        });
    }, [applyApiErrors, clearErrors, getValues, validateRequiredForCalculate]);

    const handleReset = useCallback(() => {
        reset(initialValues);
        setPartnerSuggestions([]);
        setCalcResult(null);
        setGlobalError("");
        setIsSaved(false);
        setIsApproved(false);

        toastRef.current?.show({
            severity: "info",
            summary: "Сброшено",
            detail: "Поля и результаты очищены",
            life: 2000,
        });
    }, [reset]);

    const handleSave = useCallback(() => {
        if (!calcResult) {
            return;
        }

        setIsSaved(true);
        setIsApproved(false);

        toastRef.current?.show({
            severity: "success",
            summary: "Сохранено",
            detail: "Черновик сохранен",
            life: 2000,
        });
    }, [calcResult]);

    const handleApprove = useCallback(() => {
        if (!calcResult || !isSaved) {
            return;
        }

        setIsApproved(true);

        toastRef.current?.show({
            severity: "success",
            summary: "Утверждено",
            detail: "Расчет утвержден",
            life: 2000,
        });
    }, [calcResult, isSaved]);

    const handleCancelApproval = useCallback(() => {
        if (!calcResult) {
            return;
        }

        setIsApproved(false);

        toastRef.current?.show({
            severity: "warn",
            summary: "Утверждение снято",
            detail: "Можно утвердить снова",
            life: 2200,
        });
    }, [calcResult]);

    const cards = useMemo(() => {
        const s = calcResult?.summary;
        return {
            income: s?.grossAmount ?? 0,
            retention: s?.grossRetention ?? 0,
            net: s?.total ?? 0,
        };
    }, [calcResult]);

    const breakdownRow = useMemo(() => {
        const b = calcResult?.breakdown || {};
        return [{
            opvBase: b.opv?.base ?? 0,
            opvAmount: b.opv?.amount ?? 0,
            vosmsBase: b.vosms?.base ?? 0,
            vosmsAmount: b.vosms?.amount ?? 0,
            soBase: b.so?.base ?? 0,
            soAmount: b.so?.amount ?? 0,
            ipnBase: b.ipn?.base ?? 0,
            ipnAmount: b.ipn?.amount ?? 0,
        }];
    }, [calcResult]);

    return (
        <div className={styles.page}>
            <Toast ref={toastRef} position="top-right" />

            <div className={styles.actions}>
                <Button
                    label="Рассчитать"
                    className={styles.btnCalculate}
                    onClick={handleCalculate}
                    loading={isCalculating}
                />

                <Button
                    label="Сохранить"
                    className={styles.btnSave}
                    onClick={handleSave}
                    disabled={!calcResult}
                />

                <Button
                    label="Утвердить"
                    className={styles.btnApprove}
                    onClick={handleApprove}
                    disabled={!calcResult || !isSaved || isApproved}
                />

                <Button
                    label="Отменить утверждение"
                    className={styles.btnCancelApproval}
                    onClick={handleCancelApproval}
                    disabled={!calcResult || !isApproved}
                />
            </div>

            <div className={styles.selectionPanel}>
                <div className={styles.rowTop}>
                    <div className={styles.partnerBlock}>
                        <label className={styles.label}>Партнер-перевозчик</label>

                        <Controller
                            name="partner"
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                                <AutoComplete
                                    value={field.value}
                                    suggestions={partnerSuggestions}
                                    completeMethod={onPartnerSearch}
                                    field="fullName"
                                    placeholder="Ф.И.О."
                                    className={classNames(styles.input, {
                                        "p-invalid": !!errors.partner,
                                    })}
                                    onChange={(e) => onPartnerInputChange(e.value)}
                                    onSelect={(e) => onPartnerSelect(e.value)}
                                />
                            )}
                        />
                    </div>

                    <div className={styles.iinBlock}>
                        <label className={styles.label}>ИИН</label>

                        <Controller
                            name="iin"
                            control={control}
                            render={({ field }) => (
                                <InputText
                                    {...field}
                                    className={styles.input}
                                    placeholder="Введите номер"
                                    readOnly
                                />
                            )}
                        />
                    </div>
                </div>

                <div className={styles.rowBottom}>
                    <div className={styles.statusBlock}>
                        <label className={styles.label}>Статус</label>

                        <Controller
                            name="status"
                            control={control}
                            render={({ field }) => (
                                <InputText {...field} className={styles.input} readOnly />
                            )}
                        />
                    </div>

                    <div className={styles.dateBlock}>
                        <label className={styles.label}>За период: с</label>

                        <Controller
                            name="periodFrom"
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                                <Calendar
                                    value={field.value}
                                    onChange={(e) => field.onChange(e.value)}
                                    dateFormat="dd.mm.yy"
                                    placeholder="ДД.ММ.ГГГГ"
                                    showIcon
                                    className={classNames(styles.input, {
                                        "p-invalid": !!errors.periodFrom,
                                    })}
                                />
                            )}
                        />
                    </div>

                    <div className={styles.dateBlock}>
                        <label className={styles.label}>по</label>

                        <Controller
                            name="periodTo"
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                                <Calendar
                                    value={field.value}
                                    onChange={(e) => field.onChange(e.value)}
                                    dateFormat="dd.mm.yy"
                                    placeholder="ДД.ММ.ГГГГ"
                                    showIcon
                                    className={classNames(styles.input, {
                                        "p-invalid": !!errors.periodTo,
                                    })}
                                />
                            )}
                        />
                        {!!errors.periodTo?.message && (
                            <small className={styles.fieldError}>{errors.periodTo.message}</small>
                        )}
                    </div>

                    <div className={styles.resetBlock}>
                        <Button
                            label="Сбросить"
                            className={styles.btnReset}
                            onClick={handleReset}
                            type="button"
                        />
                    </div>
                </div>

                {globalError && (
                    <div className={styles.globalError}>
                        <Message severity="error" text={globalError} />
                    </div>
                )}
            </div>

            <div className={styles.content}>
                <div className={styles.questionnaire}>
                    <div className={styles.panelTitle}>Анкетные данные:</div>

                    <div className={styles.list}>
                        {QUESTIONNAIRE_ITEMS.map((item) => {
                            const active = isQuestionnaireActive(item);

                            return (
                                <div key={`${item.type}-${item.value}`} className={styles.listRow}>
                                    <span className={styles.listLabel}>{item.label}</span>
                                    {active && (
                                        <span className={styles.checkBadge}>
                                            <i className="pi pi-check" />
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className={styles.results}>
                    <div className={styles.cardsRow}>
                        <div className={classNames(styles.card, styles.cardIncome)}>
                            <div className={styles.cardTitle}>ДОХОД</div>
                            <div className={styles.cardValue}>{formatMoneyKZT(cards.income)}</div>
                        </div>

                        <div className={classNames(styles.card, styles.cardRetention)}>
                            <div className={styles.cardTitle}>УДЕРЖАНИЯ</div>
                            <div className={styles.cardValue}>{formatMoneyKZT(cards.retention)}</div>
                        </div>

                        <div className={classNames(styles.card, styles.cardNet)}>
                            <div className={styles.cardTitle}>НА РУКИ</div>
                            <div className={styles.cardValue}>{formatMoneyKZT(cards.net)}</div>
                        </div>
                    </div>

                    <div className={styles.tableBlock}>
                        <div className={styles.tableTitle}>Детализация удержаний:</div>

                        <DataTable
                            value={breakdownRow}
                            className={styles.dataTable}
                        >
                            <Column header="База для ОПВ" field="opvBase" />
                            <Column header="ОПВ (10%)" field="opvAmount"/>

                            <Column header="База для ВОСМС" field="vosmsBase"/>
                            <Column header="ВОСМС (2%)" field="vosmsAmount" />

                            <Column header="База для СО" field="soBase" />
                            <Column header="СО (5%)" field="soAmount" />

                            <Column header="База для ИПН" field="ipnBase" />
                            <Column header="ИПН" field="ipnAmount" />
                        </DataTable>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ClcPaymentCalculator;